import { getSprintfitProductsApiUrl } from '../constants/storeUrls';

/**
 * Bundled snapshot in public/catalog-fallback.json — works on static hosting when the API is unavailable.
 * Refresh periodically from Shopify (products.json) and replace the file.
 */
function catalogFallbackUrl() {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return base ? `${base}/catalog-fallback.json` : '/catalog-fallback.json';
}

function pickRetailVariant(variants) {
  if (!variants || variants.length === 0) return {};
  let best = variants[0];
  let bestP = parseFloat(best.price);
  if (!Number.isFinite(bestP)) bestP = Infinity;
  for (let i = 1; i < variants.length; i += 1) {
    const v = variants[i];
    const p = parseFloat(v.price);
    const pp = Number.isFinite(p) ? p : Infinity;
    if (pp < bestP) {
      best = v;
      bestP = pp;
    }
  }
  return best;
}

function mapShopifyProduct(p) {
  const handle = p?.handle;
  if (!handle) return null;
  const v0 = pickRetailVariant(p.variants || []);
  const images = p.images || [];
  let price = 0;
  try {
    price = parseFloat(v0.price) || 0;
  } catch {
    price = 0;
  }
  let compareAt = null;
  if (v0.compare_at_price != null) {
    try {
      const c = parseFloat(v0.compare_at_price);
      compareAt = Number.isNaN(c) ? null : c;
    } catch {
      compareAt = null;
    }
  }
  return {
    id: p.id,
    handle,
    title: p.title || handle.replace(/-/g, ' '),
    product_url: `https://www.sprintfit.in/products/${handle}`,
    image: images[0]?.src || null,
    price,
    compare_at_price: compareAt,
    available: v0.available !== false,
  };
}

function withTimeout(ms) {
  const ctrl = new AbortController();
  const id = window.setTimeout(() => ctrl.abort(), ms);
  return { signal: ctrl.signal, cancel: () => window.clearTimeout(id) };
}

/**
 * Accepts:
 * - Normalized rows: [{ handle, price, compare_at_price, ... }, ...] (FastAPI or catalog-fallback.json)
 * - Raw Shopify catalog: { products: [...] } or [{ variants, images, ...}, ...]
 */
function normalizeCatalogPayload(data) {
  if (!data) return null;
  if (Array.isArray(data)) {
    if (data.length === 0) return null;
    const first = data[0];
    if (first && typeof first === 'object') {
      if (Array.isArray(first.variants)) {
        return data.map(mapShopifyProduct).filter(Boolean);
      }
      if (first.handle != null && first.price != null) {
        return data;
      }
    }
    return null;
  }
  if (Array.isArray(data.products)) {
    return data.products.map(mapShopifyProduct).filter(Boolean);
  }
  return null;
}

/**
 * 1) Same-origin `/api/sprintfit-products` — FastAPI (if REACT_APP_BACKEND_URL is set), or Vercel rewrite /
 *    devServer proxy → live `https://www.sprintfit.in/products.json` (see `setupProxy.js`, `vercel.json`).
 * 2) `public/catalog-fallback.json` if the request fails (offline / misconfigured deploy).
 */
export async function fetchSprintfitCatalog(limit = 48) {
  const apiUrl = `${getSprintfitProductsApiUrl()}?limit=${limit}`;
  const { signal: s1, cancel: c1 } = withTimeout(10000);
  try {
    const r = await fetch(apiUrl, { signal: s1 });
    c1();
    if (r.ok) {
      const text = await r.text();
      let data = null;
      try {
        data = JSON.parse(text);
      } catch {
        /* not JSON */
      }
      if (data) {
        const list = normalizeCatalogPayload(data);
        if (list && list.length > 0) {
          return list.slice(0, limit);
        }
      }
    }
  } catch {
    c1();
  }

  const { signal: s2, cancel: c2 } = withTimeout(8000);
  try {
    const r2 = await fetch(catalogFallbackUrl(), { signal: s2 });
    c2();
    const ct2 = r2.headers.get('content-type') || '';
    if (!r2.ok || !ct2.includes('application/json')) return null;
    const raw = await r2.json();
    const list = normalizeCatalogPayload(raw);
    if (list && list.length > 0) {
      return list.slice(0, limit);
    }
  } catch {
    c2();
  }

  return null;
}
